/* ==========================================================================
   R&T FOOD CALCULATOR — rt-food-calculator.js

   Markup: snippets/rt-food-calculator.liquid   Styles: assets/rt.css

   HOW IT WORKS
   1. Grams per day per dog: straight line between the points of that
      dog's feeding table (adult / puppy / senior). Above the last point
      the last step carries on. 12kg adult → 140 + 2/10 × 95 = 159g.
      Puppies: one table per AGE BAND (2–4, 4–6 months...), picked with
      age pills; weight = the puppy's current weight. A 0 in a table's
      grams = "use the adult amount for that weight".
   2. Grams per meal = grams per day ÷ meals a day.
   3. Each dog picks its own recipe, then a life stage. Only the stages
      that recipe comes in are shown (a recipe "has" puppy/senior when a
      product is linked in its metaobject). Dog's food = recipe + stage.
   4. The plan: for each food, every mix of bag sizes (from the real
      variants) is tried for delivery every 4 and every 5 weeks.
      Best = least spare food per delivery, with a small penalty for each
      extra bag (fewer, bigger bags are better value), then lowest price.
      All foods arrive TOGETHER on one delivery frequency. If one food's
      smallest bag lasts longer than 5 weeks (e.g. a 2kg puppy bag that
      lasts 10 weeks), deliveries stretch to that, and the other foods
      are sized to last the same time.
   5. Buttons add the plan to the basket (one-off, or with the matching
      subscription plan once the products have them).
   ========================================================================== */

(() => {
  if (customElements.get('rt-food-calculator')) return;


  /* ---------------------------------------------------------------------
     SETTINGS — tweak here
     --------------------------------------------------------------------- */

  const STAGES = ['adult', 'puppy', 'senior'];
  const STAGE_LABELS = { adult: 'Adult', puppy: 'Puppy', senior: 'Senior' };

  // Built-in tables, used until the "R&T feeding table" metaobjects exist.
  // Adult is the bag's feeding guide; puppy (½) and senior (¾) are placeholders.
  const ADULT_TABLE = { weights: [1, 5, 10, 20, 30, 40], grams: [25, 85, 140, 235, 320, 395] };
  const scaleTable = (table, factor) => ({
    weights: [...table.weights],
    grams: table.grams.map((g) => Math.round(g * factor)),
  });
  const DEFAULT_TABLES = {
    adult: ADULT_TABLE,
    puppy: scaleTable(ADULT_TABLE, 0.5),
    senior: scaleTable(ADULT_TABLE, 0.75),
  };

  const DEFAULT_BAG_SIZES_KG = [2, 6];      // only used before recipes are linked; real sizes come from the variants
  const DELIVERY_WEEKS = [4, 5];            // "roughly monthly"
  const EXTRA_BAG_PENALTY_DAYS = 3;         // each extra bag counts like 3 spare days
  const MAX_BAGS_PER_FOOD = 12;


  /* ---------------------------------------------------------------------
     HELPERS
     --------------------------------------------------------------------- */

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const formatMoney = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(cents / 100);
  };

  const formatKg = (grams) => {
    const kg = grams / 1000;
    return `${kg >= 10 ? Math.round(kg) : kg.toFixed(1)}kg`;
  };

  // Table → sorted [kg, grams] points, or null if unusable.
  // With adultPoints given, a grams value of 0 means "the adult amount
  // at that weight" (the supplier's "ADULT" cells).
  const toPoints = (table, adultPoints = null) => {
    if (!table) return null;
    const weights = (table.weights || []).map(Number);
    const grams = (table.grams || []).map(Number);
    const points = weights
      .map((w, i) => [w, grams[i]])
      .filter(([w, g]) => w > 0 && (g > 0 || (adultPoints && g === 0)))
      .map(([w, g]) => [w, g > 0 ? g : gramsPerDay(adultPoints, w)])
      .sort((a, b) => a[0] - b[0]);
    return points.length >= 2 ? points : null;
  };

  // Grams per day for a weight, on a straight line between table points
  const gramsPerDay = (points, kg) => {
    if (!(kg > 0)) return 0;

    const [firstKg, firstGrams] = points[0];
    if (kg <= firstKg) return (firstGrams * kg) / firstKg;

    for (let i = 1; i < points.length; i++) {
      const [kgA, gA] = points[i - 1];
      const [kgB, gB] = points[i];
      if (kg <= kgB) return gA + ((kg - kgA) / (kgB - kgA)) * (gB - gA);
    }

    // Heavier than the table: carry on at the last step's rate
    const [kgA, gA] = points[points.length - 2];
    const [kgB, gB] = points[points.length - 1];
    return gB + (kg - kgB) * ((gB - gA) / (kgB - kgA));
  };

  // Bag sizes on sale for a product → [{ kg, variant }]
  const bagOptions = (product) => {
    if (!product) return DEFAULT_BAG_SIZES_KG.map((kg) => ({ kg, variant: null }));

    const options = [];
    product.variants.forEach((variant) => {
      if (!variant.available) return;
      let kg = Number(variant.kg) || 0;
      if (!kg) {
        const match = /(\d+(?:\.\d+)?)\s*kg/i.exec(variant.title);
        if (match) kg = parseFloat(match[1]);
      }
      if (kg > 0) options.push({ kg, variant });
    });
    return options;
  };

  // Best mix of bags to cover `weeks` of food at `dailyGrams`
  const bestBagMix = (dailyGrams, options, weeks) => {
    const needGrams = dailyGrams * 7 * weeks;
    const sizes = [...options].sort((a, b) => b.kg - a.kg);
    const counts = new Array(sizes.length).fill(0);
    let best = null;

    const consider = (bags, grams, price) => {
      const lastsDays = grams / dailyGrams;
      const spareDays = lastsDays - 7 * weeks;
      const score = spareDays + EXTRA_BAG_PENALTY_DAYS * (bags - 1);
      const better =
        !best ||
        score < best.score - 1e-9 ||
        (Math.abs(score - best.score) < 1e-9 && price < best.price);

      if (better) {
        best = {
          score, spareDays, lastsDays, bags, price, weeks,
          lines: sizes.map((s, i) => ({ ...s, qty: counts[i] })).filter((l) => l.qty > 0),
        };
      }
    };

    // Try every count of each size (biggest first); stop adding once covered
    const walk = (index, bags, grams, price) => {
      if (grams >= needGrams) return consider(bags, grams, price);
      if (index === sizes.length) return;

      const size = sizes[index];
      for (let qty = 0; bags + qty <= MAX_BAGS_PER_FOOD; qty++) {
        counts[index] = qty;
        const newGrams = grams + qty * size.kg * 1000;
        walk(index + 1, bags + qty, newGrams, price + qty * (size.variant ? size.variant.price : 0));
        if (newGrams >= needGrams) break;
      }
      counts[index] = 0;
    };

    walk(0, 0, 0, 0);
    return best;
  };

  // Subscription plan for a variant matching the delivery frequency
  const sellingPlanObjectFor = (variant, weeks) => {
    const plans = (variant && variant.sellingPlans) || [];
    if (!plans.length) return null;
    const match = plans.find((plan) => {
      const m = /(\d+)\s*week/i.exec(plan.name);
      return m && Number(m[1]) === weeks;
    });
    return match || plans[0];
  };
  const sellingPlanFor = (variant, weeks) => {
    const plan = sellingPlanObjectFor(variant, weeks);
    return plan ? plan.id : null;
  };

  // Delivery frequencies (in weeks) a product's subscription plans offer,
  // read from plan names like "4 Week Subscription". Empty = no plans.
  const planWeeks = (product) => {
    const weeks = new Set();
    ((product && product.variants) || []).forEach((variant) => {
      (variant.sellingPlans || []).forEach((plan) => {
        const m = /(\d+)\s*week/i.exec(plan.name);
        if (m) weeks.add(Number(m[1]));
      });
    });
    return weeks;
  };

  // Subscription price for a whole plan, or null if any bag has no plan yet
  const subscriptionPrice = (plan) => {
    let total = 0;
    for (const mix of plan.mixes) {
      for (const line of mix.lines) {
        const sp = sellingPlanObjectFor(line.variant, plan.weeks);
        if (!sp || !(sp.price >= 0)) return null;
        total += sp.price * line.qty;
      }
    }
    return total;
  };


  /* ---------------------------------------------------------------------
     THE ELEMENT
     --------------------------------------------------------------------- */

  class RtFoodCalculator extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      // ---- Data from Liquid ----
      let data = {};
      try {
        data = JSON.parse(this.querySelector('[data-rt-food-data]').textContent);
      } catch (error) {
        console.warn('[R&T food calculator] Could not read data', error);
      }
      this.data = data;

      // Feeding tables: metaobject entries override the built-in ones.
      // Each stage is a list of bands: [{ label: '2–4 months', points }].
      // Adult/senior normally have one band with no label; puppy has one
      // per age band (its pills). Built-in tables fill any gaps.
      const entries = (data.tables || []).map((t) => ({
        ...t,
        stage: String(t.stage || '').toLowerCase().trim(),
        band: String(t.band || '').trim(),
        position: Number(t.position) || 0,
      }));

      const adultEntry = entries.find((e) => e.stage === 'adult');
      this.adultPoints = toPoints(adultEntry) || toPoints(DEFAULT_TABLES.adult);

      this.tables = {};
      STAGES.forEach((stage) => {
        let list = entries.filter((e) => e.stage === stage);

        // If some entries have age bands, ignore any old entry without one
        if (list.some((e) => e.band)) list = list.filter((e) => e.band);

        let bands = list
          .map((e) => ({
            label: e.band,
            position: e.position,
            points: stage === 'adult' ? toPoints(e) : toPoints(e, this.adultPoints),
          }))
          .filter((b) => b.points)
          .sort((a, b) => a.position - b.position || parseFloat(a.label) - parseFloat(b.label));

        if (!bands.length) {
          bands = [{ label: '', points: toPoints(DEFAULT_TABLES[stage], this.adultPoints) }];
        }
        this.tables[stage] = bands;
      });

      // Recipes: from metaobjects, sorted; a blank one if none set up yet
      this.recipes = (data.recipes || [])
        .filter((r) => r && r.stages && (r.stages.adult || r.stages.puppy || r.stages.senior))
        .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0));
      this.hasProducts = this.recipes.length > 0;
      if (!this.hasProducts) {
        this.recipes = [{ name: '', stages: { adult: null, puppy: null, senior: null } }];
      }

      // ---- Elements ----
      this.form = this.querySelector('.rt-food-calc__form');
      this.dogs = Array.from(this.querySelectorAll('[data-dog]'));
      this.resultsBody = this.querySelector('[data-results-body]');
      this.resultsSuffix = this.querySelector('[data-results-suffix]');

      this.setupRecipes();
      this.startOnCurrentProduct();
      this.dogs.forEach((dog) => this.syncStages(dog));

      this.form.addEventListener('input', () => this.update());
      this.form.addEventListener('change', () => this.update());
      this.form.addEventListener('submit', (event) => event.preventDefault());
      this.resultsBody.addEventListener('click', (event) => this.onResultsClick(event));

      this.update();
    }

    // Recipe dropdown in every dog (hidden with one recipe, or when turned off)
    setupRecipes() {
      const options = this.recipes
        .map((recipe, i) => `<option value="${i}">${escapeHtml(recipe.name)}</option>`)
        .join('');
      const show = this.recipes.length > 1 && !this.hasAttribute('data-hide-recipe');

      this.dogs.forEach((dog) => {
        dog.querySelector('[data-recipe]').innerHTML = options;
        dog.querySelector('[data-recipe-field]').hidden = !show;
      });
    }

    recipeFor(dog) {
      return this.recipes[Number(dog.querySelector('[data-recipe]').value) || 0];
    }

    // Show only the life stages this dog's recipe comes in.
    // If the chosen stage just disappeared, fall back to the first one left.
    syncStages(dog) {
      const recipe = this.recipeFor(dog);
      const inputs = Array.from(dog.querySelectorAll('[data-stage]'));

      inputs.forEach((input) => {
        const offered = !this.hasProducts || Boolean(recipe.stages[input.value]);
        input.disabled = !offered;
        input.hidden = !offered;
        input.nextElementSibling.hidden = !offered;
      });

      const checked = inputs.find((input) => input.checked);
      if (!checked || checked.disabled) {
        const first = inputs.find((input) => !input.disabled);
        if (first) first.checked = true;
      }
    }

    // On a product page, every dog starts on that product's recipe,
    // and dog 1 on its life stage
    startOnCurrentProduct() {
      const id = this.data.currentProductId;
      if (!id || !this.hasProducts) return;

      this.recipes.forEach((recipe, index) => {
        STAGES.forEach((stage) => {
          const product = recipe.stages[stage];
          if (!product || product.id !== id) return;

          this.dogs.forEach((dog) => { dog.querySelector('[data-recipe]').value = String(index); });
          const first = this.dogs[0] && this.dogs[0].querySelector(`[data-stage][value="${stage}"]`);
          if (first) first.checked = true;
        });
      });
    }

    // Age pills for this dog's life stage (only when that stage has
    // more than one age band). Rebuilt when the stage changes.
    syncAges(dog, stage) {
      const bands = this.tables[stage];
      const field = dog.querySelector('[data-age-field]');
      const options = dog.querySelector('[data-age-options]');
      const show = bands.length > 1;

      field.hidden = !show;
      if (!show) {
        options.innerHTML = '';
        dog.dataset.ageStage = '';
        return;
      }

      if (dog.dataset.ageStage !== stage) {
        const name = `${this.dataset.uid}-age-${dog.dataset.dog}`;
        options.innerHTML = bands
          .map((band, i) => `
            <input class="rt-food-calc__segment-input" type="radio" id="${name}-${i}" name="${name}" value="${i}" data-age ${i === 0 ? 'checked' : ''}>
            <label class="rt-food-calc__segment" for="${name}-${i}">${escapeHtml(band.label)}</label>`)
          .join('');
        dog.dataset.ageStage = stage;
      }
    }

    pointsFor(dog, stage) {
      const bands = this.tables[stage];
      const checked = dog.querySelector('[data-age]:checked');
      const index = checked ? Number(checked.value) : 0;
      return (bands[index] || bands[0]).points;
    }

    get dogCount() {
      const checked = this.querySelector('[data-count]:checked');
      return checked ? Number(checked.value) : 1;
    }

    // The product a dog eats: its recipe in its life stage
    foodFor(dog, stage) {
      const recipe = this.recipeFor(dog);
      return { recipe, product: this.hasProducts ? recipe.stages[stage] || null : null };
    }

    update() {
      const count = this.dogCount;
      const groups = new Map();   // one per food (product)
      const notes = new Set();
      let totalDaily = 0;

      this.dogs.forEach((dog, index) => {
        const active = index < count;

        // A dog appearing for the first time starts on dog 1's recipe
        if (active && !dog.dataset.shown && index > 0) {
          dog.querySelector('[data-recipe]').value = this.dogs[0].querySelector('[data-recipe]').value;
        }
        if (active) dog.dataset.shown = 'true';
        this.syncStages(dog);

        dog.hidden = !active;
        dog.querySelector('[data-dog-title]').textContent = count === 1 ? 'Your dog' : `Dog ${index + 1}`;
        if (!active) return;

        const stage = (dog.querySelector('[data-stage]:checked') || {}).value || 'adult';
        this.syncAges(dog, stage);

        // Puppy tables go by the puppy's weight NOW; others by ideal weight
        dog.querySelector('[data-weight-label]').textContent =
          stage === 'puppy' ? 'Current weight (kg)' : 'Ideal weight (kg)';

        const kg = parseFloat(dog.querySelector('[data-weight]').value);
        const meals = Number(dog.querySelector('[data-meals]').value) || 1;
        const result = dog.querySelector('[data-dog-result]');

        if (!(kg > 0)) {
          result.textContent = '';
          return;
        }

        const daily = Math.round(gramsPerDay(this.pointsFor(dog, stage), kg));
        result.innerHTML = `<strong>${daily}g</strong> a day · <strong>${Math.round(daily / meals)}g</strong> per meal`;
        totalDaily += daily;

        const food = this.foodFor(dog, stage);
        if (this.hasProducts && !food.product) return;

        const key = food.product ? food.product.id : `${food.recipe.name}-${stage}`;
        if (!groups.has(key)) {
          const label = [food.recipe.name, STAGE_LABELS[stage]].filter(Boolean).join(' · ');
          groups.set(key, { label, product: food.product, daily: 0 });
        }
        groups.get(key).daily += daily;
      });

      this.render(totalDaily, Array.from(groups.values()), Array.from(notes));
    }

    // One delivery frequency for every food, so everything arrives together.
    //   Normally: every 4 or 5 weeks, whichever fits best.
    //   But if a food's SMALLEST bag lasts longer than 5 weeks, the whole
    //   delivery stretches to that many weeks (we can't send less than one
    //   bag), and every other food is sized to last the same time.
    planFor(groups) {
      let slowestWeeks = 0;
      groups.forEach((group) => {
        const sizes = bagOptions(group.product).map((o) => o.kg);
        if (!sizes.length) return;
        const smallestLastsDays = (Math.min(...sizes) * 1000) / group.daily;
        slowestWeeks = Math.max(slowestWeeks, Math.floor(smallestLastsDays / 7));
      });

      const maxNormal = Math.max(...DELIVERY_WEEKS);
      let candidates = slowestWeeks > maxNormal ? [slowestWeeks] : DELIVERY_WEEKS;

      // If the foods have subscription plans, only recommend frequencies
      // that ALL of them offer, so the plan matches what lands in the cart.
      const offered = groups
        .map((group) => planWeeks(group.product))
        .filter((set) => set.size)
        .reduce((common, set) => (common ? new Set([...common].filter((w) => set.has(w))) : set), null);

      if (offered && offered.size && groups.every((group) => planWeeks(group.product).size)) {
        const available = [...offered].sort((a, b) => a - b);
        const matching = candidates.filter((w) => offered.has(w));

        if (matching.length) {
          candidates = matching;
        } else if (slowestWeeks > maxNormal) {
          // Stretched: the first offered frequency long enough, else the longest offered
          candidates = [available.find((w) => w >= slowestWeeks) || available[available.length - 1]];
        } else {
          // Closest offered frequency to "monthly"
          candidates = [available.reduce((a, b) => (Math.abs(b - 4.5) < Math.abs(a - 4.5) ? b : a))];
        }
      }

      let best = null;

      candidates.forEach((weeks) => {
        const mixes = groups.map((group) => bestBagMix(group.daily, bagOptions(group.product), weeks));
        if (mixes.some((mix) => !mix)) return;

        const score = mixes.reduce((sum, mix) => sum + mix.score, 0);
        if (!best || score < best.score) best = { weeks, score, mixes, stretched: weeks > maxNormal };
      });

      return best;
    }

    render(totalDaily, groups, notes) {
      if (!totalDaily) {
        this.resultsBody.innerHTML =
          `<p class="rt-food-calc__empty">Add your dog's weight to see how much to feed and the best bag size.</p>`;
        this.resultsSuffix.textContent = '';
        this.plan = null;
        return;
      }

      const plan = groups.length ? this.planFor(groups) : null;
      this.plan = plan;
      this.groups = groups;

      let html = `
        <div class="rt-food-calc__total">
          <span class="rt-food-calc__figure">${totalDaily}g</span> a day
          <span class="rt-food-calc__sep" aria-hidden="true">·</span>
          <span class="rt-food-calc__figure">${formatKg(totalDaily * 28)}</span> every 4 weeks
        </div>`;

      // Title: "Your plan – Adult" when there's one food (several foods are labelled per line)
      this.resultsSuffix.textContent = groups.length === 1 && groups[0].label ? ` – ${groups[0].label}` : '';

      if (plan) {
        const price = plan.mixes.reduce((sum, mix) => sum + mix.price, 0);
        const single = plan.mixes.length === 1;

        //   We recommend
        //   2 × 2kg bags delivered every 5 weeks
        //   Lasts 42 days
        html += '<div class="rt-food-calc__plan">';
        html += '<p class="rt-food-calc__plan-intro">We recommend</p>';
        html += '<ul class="rt-food-calc__plan-list">';
        plan.mixes.forEach((mix, i) => {
          const bags = mix.lines.map((line) => `${line.qty} × ${line.kg}kg`).join(' + ');
          const word = mix.bags === 1 ? 'bag' : 'bags';
          const food = !single && groups[i].label ? ` of ${escapeHtml(groups[i].label)}` : '';
          const when = single ? ` delivered every ${plan.weeks} weeks` : '';
          html += `<li>
                     <span class="rt-food-calc__plan-line"><span class="rt-food-calc__figure">${bags}</span> ${word}${food}${when}</span>
                     <span class="rt-food-calc__plan-note">Lasts ${Math.round(mix.lastsDays)} days</span>
                   </li>`;
        });
        html += '</ul>';
        if (!single) {
          html += `<p class="rt-food-calc__plan-line">All delivered together every ${plan.weeks} weeks</p>`;
        }
        if (plan.stretched) {
          notes.push(`Every ${plan.weeks} weeks, because our smallest bag lasts that long.`);
        }
        html += '</div>';

        // Price per delivery; plus the Diners Club price once the products
        // have subscription plans (read from the app's real prices)
        if (price > 0) {
          const subPrice = subscriptionPrice(plan);
          html += `<p class="rt-food-calc__price">${formatMoney(price)} per delivery`;
          if (subPrice !== null && subPrice < price) {
            html += ` <span class="rt-food-calc__price-sub">· ${formatMoney(subPrice)} with the Diners Club</span>`;
          }
          html += '</p>';
        }
      } else if (groups.length) {
        html += `<p class="rt-food-calc__plan-intro">Sorry, that food is out of stock right now.</p>`;
      }

      if (notes.length) {
        html += `<ul class="rt-food-calc__notes">${notes.map((n) => `<li>${escapeHtml(n)}</li>`).join('')}</ul>`;
      }

      // Buttons (only when there are real products to add)
      if (plan && this.hasProducts) {
        const labels = this.data.labels || {};
        const canSubscribe = plan.mixes.every((mix) => mix.lines.every((line) => sellingPlanFor(line.variant, plan.weeks)));

        html += '<div class="rt-food-calc__actions">';
        if (canSubscribe) {
          html += `<button type="button" class="rt-button rt-button--mustard" data-add="subscribe">${escapeHtml(labels.subscribe || 'Subscribe')}</button>`;
        } else if (this.data.subscribeUrl) {
          html += `<a class="rt-button rt-button--mustard" href="${escapeHtml(this.data.subscribeUrl)}">${escapeHtml(labels.subscribe || 'Subscribe')}</a>`;
        }
        html += `<button type="button" class="rt-button rt-button--outline-cream" data-add="once">${escapeHtml(labels.add || 'One-time purchase')}</button>`;
        html += '</div><p class="rt-food-calc__status" data-status role="status"></p>';
      }

      this.resultsBody.innerHTML = html;
    }

    onResultsClick(event) {
      const button = event.target.closest('[data-add]');
      if (button) this.addToCart(button, button.dataset.add === 'subscribe');
    }

    async addToCart(button, subscribe) {
      if (!this.plan) return;

      const status = this.querySelector('[data-status]');
      const items = [];
      this.plan.mixes.forEach((mix) => {
        mix.lines.forEach((line) => {
          const item = { id: line.variant.id, quantity: line.qty };
          if (subscribe) item.selling_plan = sellingPlanFor(line.variant, this.plan.weeks);
          items.push(item);
        });
      });

      const theme = window.themeVariables || {};
      const routes = theme.routes || {};
      const cartType = (theme.settings || {}).cartType;

      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      if (status) status.textContent = '';

      try {
        const response = await fetch(`${routes.cartAddUrl || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: ['mini-cart'] }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Could not add to basket');

        if (cartType === 'page') {
          window.location.href = routes.cartUrl || '/cart';
          return;
        }

        // Same events the theme's own add-to-cart fires: refresh count + drawer
        fetch(`${routes.cartUrl || '/cart'}.js`)
          .then((r) => r.json())
          .then((cart) => document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } })));

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: result, openMiniCart: cartType === 'drawer' },
        }));

        if (status) status.textContent = 'Added to your basket.';
      } catch (error) {
        if (status) status.textContent = error.message;
      } finally {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    }
  }

  customElements.define('rt-food-calculator', RtFoodCalculator);
})();
